import { useEffect, useState } from "react";
import { safeParseJson } from "src/handlers/Projects/schema";

const SESSION_KEY = "sessioninfo";
const USER_KEY = "userinfo";
const AUTH_CHANGED_EVENT = "netbase:authStateChanged";

function readAuthState() {
  const session = safeParseJson(localStorage.getItem(SESSION_KEY), null);
  const user = safeParseJson(localStorage.getItem(USER_KEY), null);
  return {
    isSignedIn: Boolean(session && user),
    session,
    user
  };
}

/**
 * Notify listeners that the auth state has changed. Callers should invoke
 * this after writing/removing `sessioninfo` / `userinfo` so any
 * `useAuthState` consumer rerenders immediately.
 */
export function notifyAuthStateChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

/**
 * Subscribe to auth-state changes. Returns `{ isSignedIn, session, user }`
 * derived from localStorage and stays in sync across tabs via the native
 * `storage` event and same-tab via `netbase:authStateChanged`.
 */
export function useAuthState() {
  const [state, setState] = useState(readAuthState);

  useEffect(() => {
    const refresh = () => setState(readAuthState());

    const handleStorage = (event) => {
      if (event.key === null || event.key === SESSION_KEY || event.key === USER_KEY) {
        refresh();
      }
    };

    window.addEventListener(AUTH_CHANGED_EVENT, refresh);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, refresh);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return state;
}
